// Реестр dimmed-actions (handoff `concept §3` + roadmap ai-agents-new-frontend
// Артефакты спринта: «единый источник для `InProgressAction`-хука: action id →
// описание → ссылка на roadmap-задачу»).
//
// Контракт: action id (kebab-case, scoped по экрану) → описание для пользователя
// + ссылка на конкретный Шаг/Фазу спринта, на котором функционал «оживёт».
// Когда соответствующий Шаг закрывается, запись из реестра удаляется (а не
// помечается deprecated): отсутствие записи = функционал live.
//
// Расширяется по мере раскрытия экранов на Шагах 3-11 (Live-перенос и Visual shell).
// На текущей итерации (бамп `@frm/ui` + первичная intake `EditDiffConfirm`)
// реестр содержит ровно те action'ы, которые потребуются Шагу 2 (примитивы) и
// Шагу 4 (Агенты — первый dimmed-actions surface).

export interface InProgressEntry {
  /** Стабильный id, попадает в data-атрибуты для тестов и аналитики. */
  id: string
  /** Заголовок toast'а — короткий императив, что пользователь хотел сделать. */
  title: string
  /** Что именно не готово и почему. Без воды, без эмодзи. */
  description: string
  /** Шаг roadmap'а, на котором появится: «Шаг N / Фаза K». */
  roadmapStep: string
}

export const IN_PROGRESS_REGISTRY: Record<string, InProgressEntry> = {
  // Глобальные действия топбара / каркаса — Шаг 1 каркаса оставил как dimmed.
  'topbar.notifications': {
    id: 'topbar.notifications',
    title: 'Уведомления',
    description:
      'Notifications API на стороне платформы пока не определён (handoff §1). Раздел появится после спецификации канала уведомлений.',
    roadmapStep: 'Версия 2 / отдельный спринт notifications',
  },

  // Экран Агенты — Шаг 4 (live-перенос) частично заменит на реальные endpoints,
  // часть останется dimmed до версии 2 (release/model history).
  'agents.release': {
    id: 'agents.release',
    title: 'Выпустить версию агента',
    description:
      'Inline-actions Выпустить/Подтянуть привязываются к release-pipeline платформы — пока нет endpoint в transport/web/server.ts.',
    roadmapStep: 'Шаг 4 / Фаза 2 (live-перенос) + dimmed до Версии 2',
  },
  'agents.model.change': {
    id: 'agents.model.change',
    title: 'Сменить модель',
    description:
      'Смена модели агента проходит через edit→diff→confirm; endpoint появится в Шаге 4 (агент-карточка).',
    roadmapStep: 'Шаг 4 / Фаза 2',
  },

  // Экран Сессии — read API уже live; runtime mutation endpoints существуют,
  // но backend честно возвращает 501 not_implemented, пока нет session-control layer.
  'sessions.refresh': {
    id: 'sessions.refresh',
    title: 'Обновить сессии',
    description:
      'Live-реестр читает GET /api/sessions. Эта dimmed-запись оставлена только для старых shell-мест, которые ещё не используют query refresh.',
    roadmapStep: 'Шаг 10 / sessions live read',
  },
  'sessions.abort': {
    id: 'sessions.abort',
    title: 'Прервать сессию',
    description:
      'POST /api/sessions/:id/abort сейчас возвращает 501 not_implemented: нужен session-control layer поверх активного engine state.',
    roadmapStep: 'Шаг 10 / runtime mutations',
  },
  'sessions.transfer': {
    id: 'sessions.transfer',
    title: 'Перенести сессию',
    description:
      'Transfer targets читаются live, но diff/confirm endpoints пока возвращают 501 not_implemented.',
    roadmapStep: 'Шаг 10 / runtime mutations',
  },
  'sessions.permission.approve': {
    id: 'sessions.permission.approve',
    title: 'Разрешить запрос прав',
    description:
      'Live flow включён: экран Сессии показывает pending permission из /api/sessions, а POST /api/sessions/:id/permission применяет allow для текущего web-run.',
    roadmapStep: 'Helpdesk / owner-web permissions',
  },
  'sessions.permission.reject': {
    id: 'sessions.permission.reject',
    title: 'Отклонить запрос прав',
    description:
      'Live flow включён: reject отправляет deny в backend, очищает pending после refresh и показывает ошибку, если запрос уже устарел.',
    roadmapStep: 'Helpdesk / owner-web permissions',
  },

  // Экран Проекты — Шаг 7 строит клиентский visual shell. Live endpoint'ы
  // `/api/projects`, release/defer commits и web-chat session creation ещё не
  // реализованы в backend.
  'projects.openChat': {
    id: 'projects.openChat',
    title: 'Открыть проект в чате',
    description:
      'Создание web-сессии с предустановленным project/sprint требует POST /api/chat/sessions; пока доступен только shell.',
    roadmapStep: 'Шаг 7 / Фаза 3 + Шаг 10',
  },
  'projects.commit.release': {
    id: 'projects.commit.release',
    title: 'Выпустить коммит',
    description:
      'Release action должен вызывать backend git push/deploy endpoint для конкретного проекта; endpoint /api/projects/:id/commits/:hash/release пока отсутствует.',
    roadmapStep: 'Шаг 7 / Фаза 3',
  },
  'projects.commit.defer': {
    id: 'projects.commit.defer',
    title: 'Отложить коммит',
    description:
      'Defer action должен сохранять решение в release queue проекта; backend endpoint пока отсутствует.',
    roadmapStep: 'Шаг 7 / Фаза 3',
  },

  // Экран Релизы — карта версий уже читается из live read-model; правки
  // функций/привязок остаются отдельным подтверждаемым follow-up.
  'releases.promise.edit': {
    id: 'releases.promise.edit',
    title: 'Изменить promise/date',
    description:
      'Обещание и дата версии меняются только отдельным подтверждаемым решением, чтобы не потерять историю обещаний релиза.',
    roadmapStep: 'Шаг 9 / Фаза 3',
  },
  'releases.function.status': {
    id: 'releases.function.status',
    title: 'Сменить статус функции',
    description:
      'Изменение статуса функции будет отдельным flow с подтверждением и записью в журнал релиза.',
    roadmapStep: 'Шаг 9 / Фаза 3',
  },
  'releases.function.edit': {
    id: 'releases.function.edit',
    title: 'Править функцию',
    description:
      'Правка названия, описания и раздела функции требует отдельного audit-flow; текущий экран показывает read-only карту.',
    roadmapStep: 'Шаг 9 / Фаза 3',
  },
  'releases.function.add': {
    id: 'releases.function.add',
    title: 'Добавить функцию',
    description:
      'Добавление функции релиза будет оформляться отдельным подтверждаемым действием, а не быстрым inline-изменением.',
    roadmapStep: 'Шаг 9 / Фаза 3',
  },

  // Экран Чат — live text/history/SSE/cancel/command API уже подключены.
  // В registry остаются только будущие углубления визуализации и опасные команды,
  // которые нельзя имитировать без отдельного backend/audit flow.
  'chat.send': {
    id: 'chat.send',
    title: 'Отправить сообщение',
    description:
      'Text send уже live через /api/chat/:agentId/messages; этот marker сохранён только для старых shell-элементов.',
    roadmapStep: 'Шаг 10 / Фаза 3',
  },
  'chat.cancel': {
    id: 'chat.cancel',
    title: 'Остановить run',
    description:
      'Cancel уже live через /api/chat/:agentId/cancel; этот marker сохранён только для старых shell-элементов.',
    roadmapStep: 'Шаг 10 / Фаза 3',
  },
  'chat.command.checkpoint': {
    id: 'chat.command.checkpoint',
    title: 'Запустить checkpoint',
    description:
      'Web commands уже идут через /api/chat/:agentId/command. Полный checkpoint approval UI остаётся отдельным follow-up.',
    roadmapStep: 'Шаг 10 / Фаза 3',
  },
  'chat.mode.change': {
    id: 'chat.mode.change',
    title: 'Сменить режим',
    description:
      'Mode commands chat/plan/act отправляются через /api/chat/:agentId/command. Режим auto запрещён на общем VPS без sandbox-изоляции.',
    roadmapStep: 'Шаг 10 / Фаза 3',
  },
  'chat.tool.expand': {
    id: 'chat.tool.expand',
    title: 'Открыть tool-call',
    description:
      'Tool-call blocks будут заполняться из streaming trace/SSE. Сейчас показан только read-only пример структуры.',
    roadmapStep: 'Шаг 10 / Фаза 3',
  },

  // Экран Хранилища — read API `/api/vaults*` уже live. Runtime-мутации
  // sync/edit/delete пока остаются dimmed и не выполняют git-операции.
  'vaults.refresh': {
    id: 'vaults.refresh',
    title: 'Обновить хранилища',
    description:
      'Live список хранилищ уже читается из GET /api/vaults; refresh делает обычный refetch.',
    roadmapStep: 'Шаг 11 / Фаза 3',
  },
  'vaults.pull': {
    id: 'vaults.pull',
    title: 'Подтянуть хранилище',
    description:
      'Pull должен выполняться backend endpoint POST /api/vaults/:id/pull с проверкой владельца и git-result.',
    roadmapStep: 'Шаг 11 / Фаза 3',
  },
  'vaults.edit': {
    id: 'vaults.edit',
    title: 'Редактировать хранилище',
    description:
      'Add/edit проходит через diff→confirm и write-only token field. Endpoint /api/vaults/:id/diff ещё не готов.',
    roadmapStep: 'Шаг 11 / Фаза 3',
  },
  'vaults.delete': {
    id: 'vaults.delete',
    title: 'Удалить хранилище',
    description:
      'Удаление RW-хранилища owner-only и требует audit/confirm. DELETE /api/vaults/:id пока отсутствует.',
    roadmapStep: 'Шаг 11 / Фаза 3',
  },
  'vaults.file.open': {
    id: 'vaults.file.open',
    title: 'Открыть файл',
    description:
      'FileViewer уже читает GET /api/vaults/:id/file?path=…; запись файлов остаётся вне scope.',
    roadmapStep: 'Шаг 11 / Фаза 3',
  },
}

export function getInProgressEntry(id: string): InProgressEntry | undefined {
  return IN_PROGRESS_REGISTRY[id]
}
